/**
 * Coups de pouce — Attendu 1076 « Reconnaître un triangle isocèle, équilatéral
 * ou rectangle à partir d'un schéma codé ».
 *
 * Entrée PARTAGÉE par les deux formats de lecture (nature_qcm_unique,
 * nature_classer) : même geste — lire le codage, jamais l'allure. L'exemple
 * (revue 2026-09-02) est le piège central de la carte : un triangle DESSINÉ
 * comme un équilatéral, codé sur deux côtés seulement → un triangle isocèle.
 * SVG pas à pas piloté par les flèches de la sheet (data-etapes / data-caption).
 *
 * codage_qcm_unique : entrée dédiée — trier les propositions (allure, règle,
 * codage insuffisant) pour ne garder que le codage visible ET suffisant.
 */

const SVG_LIRE_CODAGE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Lire le codage d'un triangle, en 3 étapes à faire défiler avec les flèches. Un triangle PQR, dessiné avec trois côtés qui semblent de même longueur, porte le même trait sur [PQ] et sur [PR] seulement. Un petit carré à un sommet ? Non. Le même trait sur deux côtés ? Oui, [PQ] et [PR]. Le même trait sur les trois côtés ? Non, le côté [QR] n'est pas codé, même s'il a l'air aussi long. D'après le codage, c'est un triangle isocèle. Ce qui n'est pas codé ne se suppose jamais">
  <style>
    @keyframes cp1076-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1076-pop { opacity: 0; animation: cp1076-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Deux côtés codés, le troisième a l'air aussi long</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Carré : non. Deux côtés : oui. Trois côtés : non</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">D'après le codage : un triangle isocèle</text>

  <!-- ── La figure codée (étape 1) ───────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <path d="M 87.3 78.3 L 46.9 178.4 L 153.8 163.4 Z" fill="#eff6ff" stroke="#1E3A8A" stroke-width="2.2"/>
    <line x1="72.7" y1="130.6" x2="61.5" y2="126.1" stroke="#111827" stroke-width="2"/>
    <line x1="125.3" y1="117.1" x2="115.8" y2="124.5" stroke="#111827" stroke-width="2"/>
    <text x="85.2" y="68.4" font-size="12.5" font-weight="700" fill="#1e293b" text-anchor="middle">P</text><text x="35.0" y="192.6" font-size="12.5" font-weight="700" fill="#1e293b" text-anchor="middle">Q</text><text x="167.7" y="174.0" font-size="12.5" font-weight="700" fill="#1e293b" text-anchor="middle">R</text>
  </g>

  <!-- ── Les trois questions (étape 2) ───────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1076-pop">
      <line x1="72.7" y1="130.6" x2="61.5" y2="126.1" stroke="#1D9E75" stroke-width="3"/>
      <line x1="125.3" y1="117.1" x2="115.8" y2="124.5" stroke="#1D9E75" stroke-width="3"/>
      <line x1="46.9" y1="178.4" x2="153.8" y2="163.4" stroke="#B45309" stroke-width="2.6" stroke-dasharray="6 4"/>
      <text x="186" y="78" font-size="12" font-weight="700" fill="#B45309">Un petit carré à un</text>
      <text x="186" y="93" font-size="12" font-weight="700" fill="#B45309">sommet ? NON ✗</text>
      <text x="186" y="117" font-size="12" font-weight="700" fill="#1D9E75">Le même trait sur [PQ]</text>
      <text x="186" y="132" font-size="12" font-weight="700" fill="#1D9E75">et sur [PR] ? OUI ✓</text>
      <text x="186" y="156" font-size="12" font-weight="700" fill="#B45309">Le même trait sur les</text>
      <text x="186" y="171" font-size="12" font-weight="700" fill="#B45309">trois côtés ? NON ✗</text>
      <text x="186" y="187" font-size="11" font-weight="600" fill="#B45309">([QR] n'est pas codé, même</text>
      <text x="186" y="200" font-size="11" font-weight="600" fill="#B45309">s'il a l'air aussi long)</text>
    </g>
  </g>

  <!-- ── La conclusion (étape 3) ─────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1076-pop">
      <rect x="186" y="208" width="150" height="22" rx="8" fill="#ecfdf5" stroke="#1D9E75"/>
      <text x="261" y="223" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">un triangle isocèle</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Ce qui n'est pas codé ne se suppose jamais.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const SVG_BONNE_RAISON = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Trouver le codage qui établit une nature, en 3 étapes à faire défiler avec les flèches. Un triangle PQR porte un petit carré d'angle droit en P et le même trait sur [PQ] et sur [PR]. On sait qu'il est rectangle isocèle. Trois propositions sont éliminées : il a l'air rectangle isocèle, car l'allure ne prouve rien ; à la règle PQ et PR font 4 centimètres, car mesurer n'est pas une raison ; le petit carré en P à lui seul, car il dit rectangle mais pas isocèle. La bonne raison : le petit carré en P et le même trait sur [PQ] et [PR]. Une raison, c'est un codage visible et suffisant">
  <style>
    @keyframes cp1076b-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1076b-pop { opacity: 0; animation: cp1076b-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">PQR est rectangle isocèle. Quel codage le dit ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="2" font-family="system-ui,sans-serif">On élimine ce qui n'est pas un codage suffisant</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">La bonne raison : le carré ET les deux traits</text>

  <!-- ── La figure codée (étape 1) ───────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <path d="M 57.5 108.4 L 135.7 125.0 L 40.8 186.6 Z" fill="#eff6ff" stroke="#1E3A8A" stroke-width="2.2"/>
    <line x1="97.8" y1="110.8" x2="95.3" y2="122.6" stroke="#111827" stroke-width="2"/>
    <line x1="55.0" y1="148.7" x2="43.3" y2="146.3" stroke="#111827" stroke-width="2"/>
    <polyline points="68.2,110.7 65.9,121.4 55.2,119.1" fill="none" stroke="#111827" stroke-width="1.8"/>
    <text x="49.3" y="100.8" font-size="12.5" font-weight="700" fill="#1e293b" text-anchor="middle">P</text><text x="150.2" y="126.2" font-size="12.5" font-weight="700" fill="#1e293b" text-anchor="middle">Q</text><text x="31.5" y="203.4" font-size="12.5" font-weight="700" fill="#1e293b" text-anchor="middle">R</text>
  </g>

  <!-- ── On élimine (étape 2) ────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1076b-pop">
      <text x="160" y="62" font-size="12" font-weight="700" fill="#B45309" text-decoration="line-through">Il a l'air rectangle isocèle.</text>
      <text x="160" y="76" font-size="11" font-weight="600" fill="#B45309">✗ l'allure ne prouve rien</text>
      <text x="160" y="100" font-size="12" font-weight="700" fill="#B45309" text-decoration="line-through">À la règle, PQ = PR = 4 cm.</text>
      <text x="160" y="114" font-size="11" font-weight="600" fill="#B45309">✗ mesurer n'est pas une raison</text>
      <text x="160" y="138" font-size="12" font-weight="700" fill="#B45309" text-decoration="line-through">Le carré en P, à lui seul.</text>
      <text x="160" y="152" font-size="11" font-weight="600" fill="#B45309">✗ rectangle, oui ; isocèle, non</text>
    </g>
  </g>

  <!-- ── La bonne raison (étape 3) ───────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1076b-pop">
      <line x1="97.8" y1="110.8" x2="95.3" y2="122.6" stroke="#1D9E75" stroke-width="3"/>
      <line x1="55.0" y1="148.7" x2="43.3" y2="146.3" stroke="#1D9E75" stroke-width="3"/>
      <polyline points="68.2,110.7 65.9,121.4 55.2,119.1" fill="none" stroke="#1D9E75" stroke-width="2.6"/>
      <rect x="160" y="166" width="194" height="52" rx="8" fill="#ecfdf5" stroke="#1D9E75"/>
      <text x="257" y="182" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">Le petit carré en P</text>
      <text x="257" y="196" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">et le même trait</text>
      <text x="257" y="210" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">sur [PQ] et [PR]. ✓</text>
      <text x="180" y="240" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Une raison, c'est un codage visible et suffisant.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_NATURE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Seul le codage compte** : ce que le dessin a l'air d'être ne prouve rien.",
                '**Deux côtés avec le même trait** : le triangle est **isocèle**.',
                '**Les trois côtés avec le même trait** : il est **équilatéral**.',
                '**Un petit carré à un sommet** : il est **rectangle**.',
                '**Un petit carré et le même trait sur les deux côtés de ce sommet** : il est **rectangle isocèle**.',
                '**Rien de tout cela** : on ne peut rien affirmer de plus.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_LIRE_CODAGE },
    ],
};

const AIDE_CODAGE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Une bonne raison est un codage que tu vois sur la figure** : un trait, ou un petit carré.',
                "**L'allure du dessin n'est pas une raison** : « il a l'air isocèle » ne prouve rien.",
                "**Mesurer n'est pas une raison** : la figure n'est pas à l'échelle.",
                '**Vérifie que le codage cité est bien là** : les bons côtés, le bon sommet.',
                "**Vérifie qu'il suffit** : un seul trait ne compare rien, un petit carré seul ne dit pas « isocèle ».",
            ],
        },
        { titre: 'Un exemple', svg: SVG_BONNE_RAISON },
    ],
};

const CP_1076 = {
    nature_qcm_unique: AIDE_NATURE,
    nature_classer: AIDE_NATURE,
    codage_qcm_unique: AIDE_CODAGE,
};

export default CP_1076;
